/**
 * Normalise un payload QCM TanoEd vers un format stable consommable par `PrintQcm`.
 *
 * Le payload brut peut contenir des choix sous plusieurs formes :
 *   - string simple (texte ou KaTeX `$...$`)
 *   - { type: 'image',      config: { imageData | imageUrl, ... } }
 *   - { type: 'expression', config: { contenu: 'LaTeX' } }
 *   - { type: 'numberline', ... }        (Phase 3 — warn + placeholder)
 *   - { type: 'geogebra',   ... }        (Phase 3 — warn + placeholder)
 *
 * Les choix sont discriminés en `kind: 'text' | 'image'`. Pour `expression`,
 * on wrappe le contenu en `$...$` pour que le renderer passe par KaTeX.
 *
 * Sortie :
 *   {
 *     num, question, options[], correctLetters[], multi, hasImages
 *   }
 *   où options[i] = { letter, kind, content, alt? }
 */

const LETTRES = 'ABCDEFGH';

const resolveImageSrc = (config = {}) => {
    if (config.imageUrl) return config.imageUrl;
    if (config.imageData) {
        // imageData peut être un data URL complet OU du base64 brut
        return config.imageData.startsWith('data:')
            ? config.imageData
            : `data:image/png;base64,${config.imageData}`;
    }
    return null;
};

const normalizeChoix = (choix, letter) => {
    // String → texte (peut contenir du KaTeX $...$)
    if (typeof choix === 'string') {
        return { letter, kind: 'text', content: choix };
    }
    if (!choix || typeof choix !== 'object') {
        return { letter, kind: 'text', content: String(choix ?? '') };
    }
    const { type, config } = choix;

    if (type === 'image') {
        const src = resolveImageSrc(config);
        if (!src) {
            // eslint-disable-next-line no-console
            console.warn('[PrintQcm] image sans src', choix);
            return { letter, kind: 'text', content: '[image manquante]' };
        }
        return { letter, kind: 'image', content: src, alt: config?.alt || `Choix ${letter}` };
    }
    if (type === 'expression') {
        const expr = config?.contenu || '';
        return { letter, kind: 'text', content: `$${expr}$` };
    }
    if (type === 'numberline' || type === 'geogebra') {
        // eslint-disable-next-line no-console
        console.warn(`[PrintQcm] type "${type}" non supporté en Phase 1`, choix);
        return { letter, kind: 'text', content: `[choix ${type} — aperçu papier Phase 3]` };
    }
    // eslint-disable-next-line no-console
    console.warn('[PrintQcm] format de choix inconnu', choix);
    return { letter, kind: 'text', content: JSON.stringify(choix) };
};

const normalizeCorrect = (br) => {
    if (br == null) return [];
    if (Array.isArray(br)) return br.map(String);
    return [String(br)];
};

export const normalizeQcm = (payload, num) => {
    const contenu = payload?.contenu || {};
    const choixArr = Array.isArray(contenu.choix) ? contenu.choix : [];
    const options = choixArr.map((c, i) => normalizeChoix(c, LETTRES[i]));
    const hasImages = options.some(o => o.kind === 'image');
    return {
        num,
        question: payload?.texte || '',
        options,
        correctLetters: normalizeCorrect(contenu.bonne_reponse),
        multi: Boolean(contenu.multi),
        hasImages,
    };
};

/**
 * Tronque un QCM normalisé à `maxOptions` choix max.
 * - Garde TOUTES les bonnes réponses en priorité
 * - Complète avec les premiers distracteurs jusqu'à `maxOptions`
 * - Préserve l'ordre relatif des options gardées (les choix étaient déjà
 *   mélangés au moment de la génération côté `makeQcm`)
 * - Ré-assigne les lettres A/B/C/D et met à jour `correctLetters`
 *
 * Usage : affichage groupé où chaque sous-question tient dans une grille 4 max.
 */
export const trimQcmOptions = (normalized, maxOptions = 4) => {
    if (!normalized?.options || normalized.options.length <= maxOptions) {
        return normalized;
    }
    const correctSet = new Set(normalized.correctLetters);
    const needed = Math.max(0, maxOptions - correctSet.size);

    // Parcours dans l'ordre original, on garde les bonnes + les N premiers distracteurs
    let distracteursRestants = needed;
    const kept = normalized.options.filter((o) => {
        if (correctSet.has(o.letter)) return true;
        if (distracteursRestants > 0) {
            distracteursRestants -= 1;
            return true;
        }
        return false;
    });

    // Ré-affectation des lettres A, B, C…
    const oldToNew = new Map();
    const relettered = kept.map((o, i) => {
        const letter = LETTRES[i];
        oldToNew.set(o.letter, letter);
        return { ...o, letter };
    });
    const newCorrect = normalized.correctLetters
        .map((l) => oldToNew.get(l))
        .filter(Boolean);

    return {
        ...normalized,
        options: relettered,
        correctLetters: newCorrect,
    };
};
